import React, { useState, useRef, useMemo, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientSelfDataContext } from '../../../context/PatientSelfDataContext';
import { useAuth } from '../../../hooks/useAuth';
import { getInformantDashboardPath } from '../../../utils/informantDashboardRouting';
import { isModularModule } from '../../../constants/assessmentModules';
import PatientHeader from './components/PatientHeader';
import PatientSettings from './components/PatientSettings';
import QuestionnaireAccordion from './components/QuestionnaireAccordion';

export default function InformantDashboard() {
  const { patientDataModel, refreshPatientData } = useContext(PatientSelfDataContext);
  const { claims } = useAuth();
  const navigate = useNavigate();

  // Informants on any subcollection-based pathway (Adult Autism, CYP ADHD, and
  // future modular pathways) belong on the modular dashboard, which reads
  // formInstances from informants/{uid}. This page renders from the legacy
  // monolithic patient document, which a modular informant does not have — so
  // failing to redirect leaves them on a permanently empty dashboard.
  useEffect(() => {
    if (isModularModule(claims?.assessmentModule)) {
      navigate(getInformantDashboardPath(claims), { replace: true });
    }
  }, [claims, navigate]);

  // Tracks whether the mount-time refreshPatientData() call has completed.
  // Until the fresh Firestore data arrives, the dashboard shows a skeleton
  // instead of rendering from stale localStorage.
  const [isRefreshing, setIsRefreshing] = useState(true);

  // Fetch fresh data on mount so the dashboard reflects progress from any
  // form saves made since the context was last loaded.  Without onSnapshot
  // this is the only point where updated progress flows into the UI.
  useEffect(() => {
    refreshPatientData().finally(() => setIsRefreshing(false));
  }, [refreshPatientData]);

  // Settings states
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Refs for auto-scroll functionality
  const scrollContainerRef = useRef(null);
  const nextQuestionnaireRef = useRef(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Custom title overrides for informant forms
  const getTitleOverride = (originalTitle) => {
    switch (originalTitle) {
      case 'Their Symptoms in Adulthood':
        return 'Their Symptoms in Adulthood';
      case 'Their Symptoms in Childhood':
        return 'Their Symptoms in Childhood';
      default:
        return originalTitle;
    }
  };

  // Generate assessment config from patient data model
  const assessmentsConfig = useMemo(() => {
    if (!patientDataModel?.dataModel?.['1_preAppointmentInformation']?.sections) {
      return [];
    }
    
    // Find the Informant Scales section
    const informantScalesSection = Object.values(patientDataModel.dataModel['1_preAppointmentInformation'].sections)
      .find(section => section.internalName === "Informant Scales");
    
    // Initialize array for all domains
    let allDomains = [];
    
    // Add Informant Scales domains if they exist
    if (informantScalesSection && informantScalesSection.domains) {
      const scalesDomains = Object.values(informantScalesSection.domains)
        .sort((a, b) => a.suggestedOrder - b.suggestedOrder) // Sort by suggested order
        .map(domain => {
          // Get form progress from patient data
          const formData = patientDataModel?.formData?.[domain.varName] || {};
          const progress = formData.progress || 0;
          
          // Get scale results if available
          const scaleResults = formData.scaleResults || null;
          
          return {
            title: getTitleOverride(domain.displayName),
            subtitle: domain.displaySubtitle || "Assessment of their ADHD symptoms",
            route: `/scales/${domain.varName}`,
            formKey: domain.varName,
            progress: progress,
            type: 'scale',
            scaleResults: scaleResults,
            suggestedOrder: domain.suggestedOrder
          };
        });
      
      allDomains = [...scalesDomains];
    }
    
    return allDomains;
  }, [patientDataModel]);

  // Create assessments with actual progress from Firestore
  const assessments = useMemo(() => {
    return assessmentsConfig.map(assessment => {
      const formData = patientDataModel?.formData?.[assessment.formKey];
      let progress = 0;
      
      // If we have form data with progress, use it
      if (formData?.progress?.progressPercentage !== undefined) {
        progress = formData.progress.progressPercentage;
      }
      
      // Format progress to be between 0 and 100
      progress = Math.min(Math.max(Math.round(progress), 0), 100);
      
      return {
        ...assessment,
        progress
      };
    });
  }, [patientDataModel, assessmentsConfig]);

  // Group related questionnaires and create hierarchical structure
  const groupedAssessments = useMemo(() => {
    const grouped = [];
    
    // Separate assessments by type
    const adhdScales = [];
    const otherScales = [];
    
    // Group ADHD-related informant scales together
    // Adulthood: BAARS-IV Current or ASRS-O (org-specific)
    // Childhood: BAARS-IV Childhood or SNAP-IV (org-specific)
    const adhdInformantForms = new Set([
      'baarsIVCurrentInformant',
      'asrsOAdulthoodInformant',
      'baarsIVChildhoodInformant',
      'snapIVChildhoodInformant'
    ]);
    
    assessments.forEach((assessment) => {
      if (adhdInformantForms.has(assessment.formKey)) {
        adhdScales.push(assessment);
      } else {
        otherScales.push(assessment);
      }
    });
    
    // Helper function to create a group
    const createGroup = (title, children, groupKey) => {
      if (children.length === 0) return null;
      
      const totalProgress = children.reduce((sum, child) => sum + child.progress, 0);
      const combinedProgress = Math.round(totalProgress / children.length);
      
      return {
        isGroup: true,
        title,
        progress: combinedProgress,
        type: 'scale-group',
        formKey: groupKey,
        route: null,
        children: children.sort((a, b) => (a.suggestedOrder || 0) - (b.suggestedOrder || 0))
      };
    };
    
    // Create ADHD symptoms group if we have ADHD scales
    if (adhdScales.length > 0) {
      const adhdGroup = createGroup('Their ADHD Symptoms', adhdScales, 'adhdSymptoms');
      if (adhdGroup) {
        grouped.push(adhdGroup);
      }
    }
    
    // Add any other individual scales
    otherScales.forEach(assessment => {
      grouped.push({
        ...assessment,
        isGroup: false
      });
    });
    
    return grouped;
  }, [assessments]);

  // Sort the grouped assessments by suggested order
  const sortedAssessments = useMemo(() => {
    const sorted = [...groupedAssessments].sort((a, b) => {
      // For groups, use the minimum suggested order of their children
      const aOrder = a.isGroup ? 
        Math.min(...a.children.map(child => child.suggestedOrder || 0)) : 
        (a.suggestedOrder || 0);
      const bOrder = b.isGroup ? 
        Math.min(...b.children.map(child => child.suggestedOrder || 0)) : 
        (b.suggestedOrder || 0);
      
      return aOrder - bOrder;
    });
    
    return sorted;
  }, [groupedAssessments]);

  // Find the next questionnaire that needs attention (updated for groups)
  const nextQuestionnaireIndex = useMemo(() => {
    // Find first questionnaire or group that's not completed
    const nextIndex = sortedAssessments.findIndex(assessment => {
      if (assessment.isGroup) {
        // Group is not complete if any child is not complete
        return assessment.children.some(child => child.progress < 100);
      } else {
        // Individual questionnaire
        return assessment.progress < 100;
      }
    });
    
    return nextIndex;
  }, [sortedAssessments]);

  // Track when data is loaded
  useEffect(() => {
    if (patientDataModel && assessments.length > 0) {
      setIsDataLoaded(true);
    }
  }, [patientDataModel, assessments]);

  // Auto-scroll to next questionnaire when data is loaded
  useEffect(() => {
    if (!isDataLoaded || !scrollContainerRef.current) return;

    // Use a timeout to ensure DOM is fully rendered and refs are available
    const scrollTimeout = setTimeout(() => {
      if (nextQuestionnaireRef.current && scrollContainerRef.current) {
        // Calculate the scroll position with offset
        const targetElement = nextQuestionnaireRef.current;
        const container = scrollContainerRef.current;
        
        // Get the target element's position relative to the container
        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Calculate scroll position with 16px offset
        const scrollOffset = 16;
        const scrollPosition = container.scrollTop + targetRect.top - containerRect.top - scrollOffset;
        
        // Smooth scroll to the calculated position
        container.scrollTo({
          top: Math.max(0, scrollPosition), // Ensure we don't scroll past the top
          behavior: 'smooth'
        });
      }
    }, 300); // Small delay to ensure everything is rendered

    return () => clearTimeout(scrollTimeout);
  }, [isDataLoaded, nextQuestionnaireIndex]);

  return (
    <div className="h-screen bg-white flex flex-col">
      {/* Fixed Header with Shadow Overlay */}
      <div className="flex-shrink-0 relative z-10">
        <PatientHeader 
          assessments={assessments}
          onSettingsClick={() => setIsSettingsOpen(true)}
          isInformant={true}
        />
        
        {/* White gradient shadow overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-4 pointer-events-none transform translate-y-full z-20">
          {/* White fade */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/60 to-transparent"></div>
        </div>
      </div>
      
      {/* Scrollable Content Area */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto relative z-0"
      >
        {/* Skeleton: accordion card placeholders — fades out once fresh data loads */}
        <div className={`px-4 pt-4 space-y-3 transition-opacity duration-300 ease-in-out ${
          isRefreshing ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden pointer-events-none'
        }`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm animate-pulse">
              <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-2/5 rounded bg-gray-200" />
                  <div className="h-3 w-1/3 rounded bg-gray-100" />
                </div>
                <div className="h-8 w-8 rounded-full bg-gray-200" />
              </div>
              <div className="mt-3 h-2 w-full rounded-full bg-gray-100" />
            </div>
          ))}
        </div>

        {/* Real content: questionnaire accordion — fades in once fresh data loads */}
        {!isRefreshing && (
          <div className="animate-fadeIn">
            <QuestionnaireAccordion 
              assessments={sortedAssessments}
              patientDataModel={patientDataModel}
              nextQuestionnaireIndex={nextQuestionnaireIndex}
              nextQuestionnaireRef={nextQuestionnaireRef}
              isInformant={true}
            />
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <PatientSettings 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        hideInformantsTab={true}
      />

      {/* Add recaptcha container */}
      <div id="recaptcha-container"></div>
    </div>
  );
}
